import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Link, useSearchParams } from 'react-router-dom';

import concesionariaVentaProduccionService from '../../../services/concesionariaVentaProduccionService';

import Toast from '../../../components/Common/Toast';

import VentaProduccionDashboardPanel from './VentaProduccionDashboardPanel';

import VentaProduccionComparePanel from './VentaProduccionComparePanel';

import { formatPos } from './ventaProduccionDashboardUtils';



const HUB_PATH = '/produccion-concesionaria/operaciones/produccion-dia';



const VIEWS = [

  { id: 'plantilla', label: 'Por plantilla' },

  { id: 'pos', label: 'Por POS' },

  { id: 'general', label: 'General' },

  { id: 'compare', label: 'Comparar POS' },

];



const VIEW_SUBTITLES = {

  plantilla: 'Métricas y cuadro de una plantilla concreta.',

  pos: 'Agregado del POS: diferencia, merma y ventas (sin desglose por columna de plantilla).',

  general: 'Todos los POS: diferencia, merma y ventas agregadas (columnas solo en vista por plantilla).',

  compare: 'Indicadores lado a lado entre varios puntos de venta.',

};



function parseView(raw) {

  return VIEWS.some((v) => v.id === raw) ? raw : 'plantilla';

}



export default function VentaProduccionDashboard() {

  const [searchParams, setSearchParams] = useSearchParams();

  const [catalog, setCatalog] = useState([]);

  const [plantillaId, setPlantillaId] = useState(() => searchParams.get('plantilla') || '');

  const [pointOfSaleId, setPointOfSaleId] = useState(() => searchParams.get('pos') || '');

  const [view, setView] = useState(() => parseView(searchParams.get('vista')));

  const [comparePosIds, setComparePosIds] = useState(() => {

    const raw = searchParams.get('compare_pos');

    if (!raw) return [];

    return raw

      .split(',')

      .map((s) => s.trim())

      .filter(Boolean);

  });

  const [loadingCatalog, setLoadingCatalog] = useState(true);

  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });



  useEffect(() => {

    concesionariaVentaProduccionService.getPlantillasCatalog().then((res) => {

      if (res.success) {

        const list = res.data || [];

        setCatalog(list);

        if (list.length === 1 && !plantillaId) {

          setPlantillaId(String(list[0].id));

        }

      }

      setLoadingCatalog(false);

    });

  }, []);



  const plantilla = useMemo(

    () => catalog.find((p) => String(p.id) === String(plantillaId)),

    [catalog, plantillaId],

  );



  const plantillaOptions = useMemo(() => {

    const groups = new Map();

    catalog.forEach((pl) => {

      const posLabel = formatPos(pl.point_of_sale);

      if (!groups.has(posLabel)) groups.set(posLabel, []);

      groups.get(posLabel).push(pl);

    });

    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], 'es'));

  }, [catalog]);



  const pointOfSaleOptions = useMemo(() => {

    const map = new Map();

    catalog.forEach((pl) => {

      const pos = pl.point_of_sale;

      if (pos?.id && !map.has(pos.id)) {

        map.set(pos.id, pos);

      }

    });

    return [...map.values()].sort((a, b) => formatPos(a).localeCompare(formatPos(b), 'es'));

  }, [catalog]);



  useEffect(() => {

    const next = new URLSearchParams();

    next.set('vista', view);

    if (view === 'plantilla' && plantillaId) next.set('plantilla', plantillaId);

    if (view === 'pos' && pointOfSaleId) next.set('pos', pointOfSaleId);

    if (view === 'compare' && comparePosIds.length) {

      next.set('compare_pos', comparePosIds.join(','));

    }

    setSearchParams(next, { replace: true });

  }, [view, plantillaId, pointOfSaleId, comparePosIds, setSearchParams]);



  const onToggleComparePos = useCallback((id) => {

    const sid = String(id);

    setComparePosIds((prev) => {

      if (prev.includes(sid)) return prev.filter((x) => x !== sid);

      if (prev.length >= 8) return prev;

      return [...prev, sid];

    });

  }, []);



  const showPanel =

    view === 'general' ||

    (view === 'plantilla' && plantillaId) ||

    (view === 'pos' && pointOfSaleId);



  const emptyHint =

    view === 'plantilla'

      ? 'Elija una plantilla para ver el dashboard.'

      : view === 'pos'

        ? 'Elija un punto de venta.'

        : null;



  return (

    <div className="p-6 max-w-6xl mx-auto">

      <nav className="text-sm text-gray-600 mb-4">

        <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>

        <span className="mx-2">/</span>

        <Link to="/produccion-concesionaria" className="hover:text-teal-600">Producción concesionaria</Link>

        <span className="mx-2">/</span>

        <span className="text-gray-800">Dashboard venta producción</span>

      </nav>



      <div className="flex flex-wrap items-start justify-between gap-4">

        <div>

          <h1 className="text-2xl font-semibold text-gray-800">Dashboard venta producción</h1>

          <p className="mt-1 text-sm text-gray-500">{VIEW_SUBTITLES[view]}</p>

        </div>

        <Link

          to={HUB_PATH}

          className="text-sm font-medium text-teal-700 border border-teal-600 rounded-md px-3 py-1.5 hover:bg-teal-50"

        >

          Ir a despachos →

        </Link>

      </div>



      <div className="mt-6 flex flex-wrap gap-2 border-b border-gray-200 pb-1">

        {VIEWS.map((v) => (

          <button

            key={v.id}

            type="button"

            onClick={() => setView(v.id)}

            className={`px-3 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors ${

              view === v.id

                ? 'border-teal-600 text-teal-800 bg-teal-50/50'

                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'

            }`}

          >

            {v.label}

          </button>

        ))}

      </div>



      {view === 'plantilla' && (

        <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">

          <label htmlFor="dash-plantilla" className="block text-sm font-medium text-gray-700 mb-1">

            Plantilla

          </label>

          {loadingCatalog ? (

            <p className="text-sm text-gray-500">Cargando plantillas…</p>

          ) : catalog.length === 0 ? (

            <p className="text-sm text-amber-800">

              No hay plantillas. Configure una en{' '}

              <Link to="/produccion-concesionaria/configuraciones/cuadro-venta" className="font-medium underline">

                Cuadro venta

              </Link>

              .

            </p>

          ) : (

            <select

              id="dash-plantilla"

              value={plantillaId}

              onChange={(e) => setPlantillaId(e.target.value)}

              className="w-full max-w-xl rounded-md border border-gray-300 px-3 py-2 text-sm bg-white"

            >

              <option value="">Seleccione plantilla…</option>

              {plantillaOptions.map(([posLabel, items]) => (

                <optgroup key={posLabel} label={posLabel}>

                  {items.map((pl) => (

                    <option key={pl.id} value={String(pl.id)}>{pl.name}</option>

                  ))}

                </optgroup>

              ))}

            </select>

          )}

          {plantilla && (

            <p className="mt-2 text-xs text-gray-500">

              {formatPos(plantilla.point_of_sale)}

              {plantilla.osv_client && (

                <>

                  {' · '}

                  Cliente OSV: {plantilla.osv_client.code ? `${plantilla.osv_client.code} — ` : ''}

                  {plantilla.osv_client.name}

                </>

              )}

              {plantilla.catalog_project && (

                <>

                  {' · '}

                  Proyecto: {plantilla.catalog_project.code ? `${plantilla.catalog_project.code} — ` : ''}

                  {plantilla.catalog_project.name}

                </>

              )}

            </p>

          )}

        </section>

      )}



      {view === 'pos' && (

        <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">

          <label htmlFor="dash-pos" className="block text-sm font-medium text-gray-700 mb-1">

            Punto de venta

          </label>

          {loadingCatalog ? (

            <p className="text-sm text-gray-500">Cargando…</p>

          ) : (

            <select

              id="dash-pos"

              value={pointOfSaleId}

              onChange={(e) => setPointOfSaleId(e.target.value)}

              className="w-full max-w-xl rounded-md border border-gray-300 px-3 py-2 text-sm bg-white"

            >

              <option value="">Seleccione POS…</option>

              {pointOfSaleOptions.map((pos) => (

                <option key={pos.id} value={String(pos.id)}>{formatPos(pos)}</option>

              ))}

            </select>

          )}

        </section>

      )}



      {view === 'compare' && (

        <div className="mt-6">

          <VentaProduccionComparePanel

            pointOfSaleOptions={pointOfSaleOptions}

            selectedPosIds={comparePosIds}

            onTogglePos={onToggleComparePos}

            idPrefix="sidebar-vp-compare"

            onLoadError={(msg) => setToast({ isOpen: true, message: msg, type: 'error' })}

          />

        </div>

      )}



      {(view === 'plantilla' || view === 'pos' || view === 'general') && (

        <div className="mt-6">

          {showPanel ? (

            <VentaProduccionDashboardPanel

              scope={view}

              plantillaId={plantillaId}

              pointOfSaleId={pointOfSaleId}

              idPrefix={`sidebar-vp-dash-${view}`}

              onLoadError={(msg) => setToast({ isOpen: true, message: msg, type: 'error' })}

            />

          ) : (

            !loadingCatalog &&

            emptyHint && (

              <p className="mt-8 text-center text-gray-500 text-sm">{emptyHint}</p>

            )

          )}

        </div>

      )}



      <Toast {...toast} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />

    </div>

  );

}


